import React from 'react';
import DateRangePresetPicker from './DateRangePresetPicker';

const PurchaseFilters = ({
  filters,
  setters,
  showSearch = false,
  showPaymentTerm = true,
  showOverdue = true,
  showDueRange = true,
  showDocumentType = false,
  documentType = '',
  onDocumentTypeChange,
  showPaymentStatus = false,
  paymentFilter = '',
  onPaymentFilterChange,
  showDateRange = true,
  useSelectFilters = false,
  layout = 'default',
  containerClassName = 'bg-gray-100',
  children,
}) => {
  const {
    startDate,
    endDate,
    paymentTermFilter,
    dueStartDate,
    dueEndDate,
    overdueFilter,
    searchTerm,
  } = filters;
  const {
    setStartDate,
    setEndDate,
    setPaymentTermFilter,
    setDueStartDate,
    setDueEndDate,
    setOverdueFilter,
    setSearchTerm,
  } = setters;

  const toneStyles = {
    gray: {
      tabActive: 'bg-gray-800 text-white border-gray-800',
      tabInactive: 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100',
    },
    amber: {
      tabActive: 'bg-amber-500 text-white border-amber-500',
      tabInactive: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100',
    },
    indigo: {
      tabActive: 'bg-indigo-500 text-white border-indigo-500',
      tabInactive: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
    },
    emerald: {
      tabActive: 'bg-emerald-500 text-white border-emerald-500',
      tabInactive: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    },
    sky: {
      tabActive: 'bg-sky-500 text-white border-sky-500',
      tabInactive: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
    },
  };
  const documentTabs = [
    { value: '', label: 'Todos', tone: 'gray' },
    { value: 'boleta', label: 'Boleta', tone: 'amber' },
    { value: 'factura', label: 'Factura', tone: 'indigo' },
  ];
  const paymentTabs = [
    { value: '', label: 'Todas', tone: 'gray' },
    { value: 'paid', label: 'Pagadas', tone: 'emerald' },
    { value: 'credit', label: 'Crédito', tone: 'sky' },
  ];
  const handleDocumentTypeClick = (value) => {
    if (typeof onDocumentTypeChange === 'function') {
      onDocumentTypeChange(value);
    }
  };
  const handlePaymentFilterClick = (value) => {
    if (typeof onPaymentFilterChange === 'function') {
      onPaymentFilterChange(value);
    }
  };

  const isRowLayout = layout === 'row';
  const containerLayoutClass = isRowLayout
    ? 'grid grid-cols-1 md:grid-cols-4 gap-4 items-end'
    : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4';
  const wideColumnClass = isRowLayout ? '' : 'md:col-span-2';

  const renderDocumentTypeFilter = () => {
    if (!showDocumentType) return null;
    if (useSelectFilters) {
      return (
        <div>
          <label className="block text-sm font-medium">Documento:</label>
          <select
            value={documentType}
            onChange={(e) => handleDocumentTypeClick(e.target.value)}
            className="border border-gray-300 rounded p-2 w-full"
          >
            {documentTabs.map((tab) => (
              <option key={tab.value || 'all'} value={tab.value}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    return (
      <div className={wideColumnClass}>
        <label className="block text-sm font-medium mb-2">Documento:</label>
        <div className="flex flex-wrap gap-2">
          {documentTabs.map((tab) => {
            const tone = toneStyles[tab.tone] || toneStyles.gray;
            const isActive = documentType === tab.value;
            return (
              <button
                key={tab.value || 'all'}
                type="button"
                onClick={() => handleDocumentTypeClick(tab.value)}
                aria-pressed={isActive}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                  isActive ? tone.tabActive : tone.tabInactive
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderPaymentStatusFilter = () => {
    if (!showPaymentStatus) return null;
    if (useSelectFilters) {
      return (
        <div>
          <label className="block text-sm font-medium">Estado de pago:</label>
          <select
            value={paymentFilter}
            onChange={(e) => handlePaymentFilterClick(e.target.value)}
            className="border border-gray-300 rounded p-2 w-full"
          >
            {paymentTabs.map((tab) => (
              <option key={tab.value || 'all'} value={tab.value}>
                {tab.label}
              </option>
            ))}
          </select>
        </div>
      );
    }
    return (
      <div className={wideColumnClass}>
        <label className="block text-sm font-medium mb-2">Estado de pago:</label>
        <div className="flex flex-wrap gap-2">
          {paymentTabs.map((tab) => {
            const tone = toneStyles[tab.tone] || toneStyles.gray;
            const isActive = paymentFilter === tab.value;
            return (
              <button
                key={tab.value || 'all'}
                type="button"
                onClick={() => handlePaymentFilterClick(tab.value)}
                aria-pressed={isActive}
                className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                  isActive ? tone.tabActive : tone.tabInactive
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${containerLayoutClass} p-4 rounded shadow ${containerClassName}`}
    >
      {showSearch && (
        <div>
          <label className="block text-sm font-medium">Buscar Cliente:</label>
          <input
            type="text"
            placeholder="Nombre, RUT o nro de documento"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border border-gray-300 rounded p-2 w-full"
          />
        </div>
      )}

      {renderDocumentTypeFilter()}

      {showDateRange && (
        <DateRangePresetPicker
          label="Rango de fechas"
          presetLabel="Rango rapido"
          startLabel="Fecha inicio"
          endLabel="Fecha fin"
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          containerClassName={wideColumnClass}
          labelClassName="block text-sm font-medium mb-1"
          inputLabelClassName="block text-xs text-gray-600 mb-1"
          selectClassName="border border-gray-300 rounded p-2 w-full"
          inputClassName="border border-gray-300 rounded p-2 w-full"
        />
      )}

      {children}

      {renderPaymentStatusFilter()}

      {showPaymentTerm && (
        <div>
          <label className="block text-sm font-medium">Condici&oacute;n:</label>
          <select
            value={paymentTermFilter}
            onChange={(e) => setPaymentTermFilter(e.target.value)}
            className="border border-gray-300 rounded p-2 w-full"
          >
            <option value="">Todas</option>
            <option value="credit">Cr&eacute;dito</option>
            <option value="immediate">Contado</option>
          </select>
        </div>
      )}

      {showOverdue && (
        <div>
          <label className="block text-sm font-medium">Vencimiento:</label>
          <select
            value={overdueFilter}
            onChange={(e) => setOverdueFilter(e.target.value)}
            className="border border-gray-300 rounded p-2 w-full"
          >
            <option value="">Todos</option>
            <option value="overdue">Vencidas</option>
            <option value="current">Al d&iacute;a</option>
          </select>
        </div>
      )}

      {showDueRange && (
        <DateRangePresetPicker
          label="Rango de vencimiento"
          presetLabel="Vence en"
          presetOptionLabel={(days) => `Vence en ${days} dias`}
          startLabel="Vence desde"
          endLabel="Vence hasta"
          direction="future"
          startDate={dueStartDate}
          endDate={dueEndDate}
          onStartDateChange={setDueStartDate}
          onEndDateChange={setDueEndDate}
          containerClassName={wideColumnClass}
          labelClassName="block text-sm font-medium mb-1"
          inputLabelClassName="block text-xs text-gray-600 mb-1"
          selectClassName="border border-gray-300 rounded p-2 w-full"
          inputClassName="border border-gray-300 rounded p-2 w-full"
        />
      )}
    </div>
  );
};

export default PurchaseFilters;
